<template>
  <div class="organization-list-view">
    <!-- Header -->
    <div class="flex justify-between items-center mb-3">
      <h1 class="text-2xl font-bold">Organizations</h1>
      <div class="flex ml-auto gap-2">
        <Button icon="pi pi-plus" label="Add Organization" @click="addNewOrganization" />
      </div>
    </div>

    <!-- Search Bar -->
    <Card class="mb-4">
      <template #content>
        <div class="flex gap-4">
          <InputText
            v-model="search"
            placeholder="Search by organization name..."
            class="flex-1"
            @input="onSearchChange"
          />
        </div>
      </template>
    </Card>

    <!-- Tabs -->
    <div class="">
      <TabView v-model:activeIndex="activeTabIndex">
        <TabPanel value="0">
          <template #header>
            <div class="flex items-center gap-2">
              <span>Customers</span>
              <Badge :value="customerCount" severity="success" />
            </div>
          </template>
        </TabPanel>
        <TabPanel value="1">
          <template #header>
            <div class="flex items-center gap-2">
              <span>Suppliers</span>
              <Badge :value="supplierCount" severity="warning" />
            </div>
          </template>
        </TabPanel>
        <TabPanel value="2">
          <template #header>
            <div class="flex items-center gap-2">
              <span>Leads</span>
              <Badge :value="leadCount" severity="help" />
            </div>
          </template>
        </TabPanel>
        <TabPanel value="3">
          <template #header>
            <div class="flex items-center gap-2">
              <span>Prospects</span>
              <Badge :value="prospectCount" severity="info" />
            </div>
          </template>
        </TabPanel>
        <TabPanel value="4">
          <template #header>
            <div class="flex items-center gap-2">
              <span>All</span>
              <Badge :value="allCount" severity="info" />
            </div>
          </template>
        </TabPanel>
      </TabView>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-8">
      <ProgressSpinner />
    </div>

    <!-- Organizations List -->
    <div v-else class="space-y-4">
      <OrganizationCard
        v-for="organization in organizations"
        :key="organization.id"
        :organization="organization"
        @view="viewOrganization"
        @edit="editOrganization"
        @delete="handleDeleteOrganization"
        class="my-1"
      />
    </div>

    <!-- Empty State -->
    <div v-if="!loading && organizations.length === 0" class="text-center py-8">
      <i class="pi pi-building text-4xl text-gray-400 mb-4"></i>
      <h3 class="text-lg font-medium text-gray-600 mb-2">No organizations found</h3>
      <p class="text-gray-500">Try adjusting your search or create a new organization.</p>
    </div>

    <!-- Load More Button -->
    <div v-if="!loading && organizations.length > 0 && hasMore" class="text-center mt-6">
      <Button label="Load More" icon="pi pi-refresh" outlined @click="loadMore" />
    </div>

    <!-- Organization Dialog -->
    <OrganizationDialog
      :visible="organizationDialogVisible"
      :organizationData="dialogOrganization"
      :isEditing="isEditing"
      @update:visible="organizationDialogVisible = $event"
      @organization-saved="onOrganizationSaved"
      @cancel="onDialogCancel"
    />
  </div>
</template>

<script setup lang="ts">
import { onMounted, computed, ref, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'primevue/usetoast'
import { useOrganizationStore } from '@/stores/v2/organization'
import { useSession } from '@/stores/authStore'
import { createOrganization } from '@/factories/organizationFactory'
import type { Organization } from '@/types/Organization'

type Session = { tenantId?: string; yardId?: string; userId?: string }

// Components
import Badge from 'primevue/badge'
import Button from 'primevue/button'
import Card from 'primevue/card'
import InputText from 'primevue/inputtext'
import ProgressSpinner from 'primevue/progressspinner'
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import OrganizationCard from '@/components/organization/OrganizationCard.vue'
import OrganizationDialog from '@/components/organization/OrganizationDialog.vue'

const router = useRouter()
const toast = useToast()
const organizationStore = useOrganizationStore()
const session = useSession()

// State
const organizations = ref<Organization[]>([])
const loading = ref(false)
const search = ref('')
const pageSize = ref(50)
const hasMore = ref(true)

// Tab state
const activeTabIndex = ref<number>(0)

// Count state
const allCount = ref(0)
const customerCount = ref(0)
const supplierCount = ref(0)
const leadCount = ref(0)
const prospectCount = ref(0)

// Debounce search
let searchTimeout: NodeJS.Timeout | null = null

// Dialog state
const organizationDialogVisible = ref(false)
const dialogOrganization = ref<Partial<Organization>>(createOrganization())
const isEditing = ref(false)

// Function to update counts
async function updateCounts() {
  try {
    const search = await organizationStore.search()

    const allResult = await search.count()
    const customerResult = await search.countByType('customer').count()
    const supplierResult = await search.countByType('supplier').count()
    const leadResult = await search.countByStatus('lead').count()
    const prospectResult = await search.countByStatus('prospect').count()

    allCount.value = allResult
    customerCount.value = customerResult
    supplierCount.value = supplierResult
    leadCount.value = leadResult
    prospectCount.value = prospectResult
  } catch (error) {
    console.error('Error updating counts:', error)
  }
}

// Load organizations based on current tab and search
async function loadOrganizations() {
  loading.value = true
  try {
    const searchInstance = await organizationStore.search()
    let searchQuery = searchInstance

    // Apply tab filter first
    const tabIndex = activeTabIndex.value ?? 0
    if (tabIndex === 0) {
      // Customers tab
      searchQuery = searchQuery.byCustomerType()
    } else if (tabIndex === 1) {
      // Suppliers tab
      searchQuery = searchQuery.bySupplierType()
    } else if (tabIndex === 2) {
      // Leads tab
      searchQuery = searchQuery.byStatus('lead')
    } else if (tabIndex === 3) {
      // Prospects tab
      searchQuery = searchQuery.byStatus('prospect')
    }
    // All tab (tabIndex === 4) - no additional filtering needed

    // Apply search filter
    if (search.value.trim()) {
      const searchTerm = search.value.trim().toLowerCase()
      // Try organization name prefix search
      searchQuery = searchQuery.prefixName(searchTerm, pageSize.value)
    } else {
      // Default to recently updated
      searchQuery = searchQuery.recentlyUpdated(pageSize.value)
    }

    const results = await searchQuery.run()
    organizations.value = results
    hasMore.value = organizations.value.length === pageSize.value
  } catch (error) {
    console.error('Error loading organizations:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load organizations',
      life: 3000,
    })
  } finally {
    loading.value = false
  }
}

// Watch for search changes with debounce
function onSearchChange() {
  if (searchTimeout) {
    clearTimeout(searchTimeout)
  }
  searchTimeout = setTimeout(() => {
    loadOrganizations()
  }, 300)
}

// Watch for tab changes
watch(activeTabIndex, () => {
  loadOrganizations()
})

// Initialize
onMounted(async () => {
  await updateCounts()
  await nextTick()
  activeTabIndex.value = 0
  await loadOrganizations()
})

function loadMore() {
  pageSize.value += 20
  loadOrganizations()
}

// Organization methods
function addNewOrganization() {
  dialogOrganization.value = createOrganization({
    type: 'supplier',
    status: 'active',
    tags: [],
  })
  isEditing.value = false
  organizationDialogVisible.value = true
}

function viewOrganization(organization: Organization) {
  if (!organization?.id) return
  router.push(`/crm/organizations/${organization.id}`)
}

function editOrganization(organization: Organization) {
  if (!organization?.id) return
  dialogOrganization.value = { ...organization }
  isEditing.value = true
  organizationDialogVisible.value = true
}

async function handleDeleteOrganization(organization: Organization) {
  if (!organization?.id) return

  try {
    await organizationStore.remove(organization.id)
    toast.add({
      severity: 'success',
      summary: 'Organization Deleted',
      detail: `${organization.name} has been deleted`,
      life: 3000,
    })
    await loadOrganizations()
    await updateCounts()
  } catch (error) {
    console.error('Error deleting organization:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to delete organization',
      life: 3000,
    })
  }
}

async function onOrganizationSaved(saved: Partial<Organization>) {
  try {
    if (saved.id) {
      await organizationStore.update(saved.id, saved)
      toast.add({
        severity: 'success',
        summary: 'Company Updated',
        detail: `${saved.name ?? 'Company'} has been updated`,
        life: 3000,
      })
    } else {
      const newId = await organizationStore.create(saved)
      toast.add({
        severity: 'success',
        summary: 'Company Created',
        detail: `${saved.name ?? 'New company'} has been created`,
        life: 3000,
      })
      // Optionally use newId later
    }
    await loadOrganizations()
    await updateCounts()
  } catch (error) {
    console.error('Error saving organization:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save organization',
      life: 3000,
    })
  }
}

function onDialogCancel() {
  // No-op for now
}
</script>

<style scoped>
.organization-list-view {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;
}

.p-card {
  border-radius: 0px;
}

/* Custom button styling for the outlined button */
:deep(.p-button-outlined) {
  border: 1px solid var(--p-primary-color);
  background: transparent;
  color: var(--p-primary-color);
  transition: all 0.2s ease-in-out;
}

:deep(.p-button-outlined:hover) {
  background: var(--p-primary-color);
  color: var(--p-primary-color-text);
  transform: scale(1.05);
}
</style>
